import type { AnswerMap, JsonValue } from './types';

export type AppointmentGroup = 'primary' | 'successor';
export type AppointmentRow = Record<string, JsonValue>;

export function appointmentGroup(row: AppointmentRow, index: number): AppointmentGroup {
	return row._group === 'primary' || row._group === 'successor' ? row._group : index === 0 ? 'primary' : 'successor';
}

export function appointmentRows(value: JsonValue | undefined): AppointmentRow[] {
	return Array.isArray(value) ? value.filter((row): row is AppointmentRow => !!row && typeof row === 'object' && !Array.isArray(row)) : [];
}

export function groupAppointments(value: JsonValue | undefined): { primary: AppointmentRow[]; successor: AppointmentRow[] } {
	const groups: { primary: AppointmentRow[]; successor: AppointmentRow[] } = { primary: [], successor: [] };
	appointmentRows(value).forEach((row, index) => groups[appointmentGroup(row, index)].push(row));
	return groups;
}

// Materialize every legacy role before any edit, especially a removal or insertion.
export function explicitAppointmentRows(value: JsonValue | undefined): AppointmentRow[] {
	return appointmentRows(value).map((row, index) => ({ ...row, _group: appointmentGroup(row, index) }));
}

interface PersonField {
	id: string;
	label: string;
	type: string;
	primaryLabel?: string;
	children?: readonly { id: string }[];
}

export interface SavedAppointmentPerson {
	id: string;
	label: string;
	full_name: string;
	address: JsonValue;
	phone: string;
	fieldId: string;
	rowIndex?: number;
}

export function reusablePersonTarget(field: PersonField): 'full_name' | 'beneficiary' | undefined {
	if (field.type !== 'repeatable' || field.id.startsWith('family.')) return undefined;
	if (field.children?.some((child) => child.id === 'beneficiary')) return 'beneficiary';
	if ((field.primaryLabel || field.id === 'planning.trust_beneficiaries') && field.children?.some((child) => child.id === 'full_name'))
		return 'full_name';
	return undefined;
}

/** Source identities are locations, never names. No appointments are inferred. */
export function savedAppointmentPeople(answers: AnswerMap, fields: readonly PersonField[]): SavedAppointmentPerson[] {
	const people: SavedAppointmentPerson[] = [];
	const add = (
		fieldId: string,
		origin: string,
		name: JsonValue | undefined,
		address: JsonValue | undefined,
		phone: JsonValue | undefined,
		rowIndex?: number,
	) => {
		if (typeof name !== 'string' || !name.trim()) return;
		people.push({
			id: JSON.stringify([fieldId, rowIndex ?? null]),
			label: `${name} — ${origin}`,
			full_name: name,
			address: structuredClone(address ?? ''),
			phone: typeof phone === 'string' ? phone : '',
			fieldId,
			rowIndex,
		});
	};
	add(
		'client',
		'Client',
		answers['client.full_name'],
		answers['client.address'],
		answers['client.best_daytime_phone'] || answers['client.mobile'] || answers['client.home_phone'],
	);
	add(
		'spouse',
		'Spouse',
		answers['spouse.full_name'],
		answers[answers['spouse.same_address'] === 'yes' ? 'client.address' : 'spouse.address'],
		answers['spouse.mobile'],
	);
	for (const field of fields) {
		if (field.type !== 'repeatable') continue;
		const target = field.id.startsWith('family.') ? 'full_name' : reusablePersonTarget(field);
		if (!target) continue;
		appointmentRows(answers[field.id]).forEach((row, index) => {
			const group = field.primaryLabel ? `, ${appointmentGroup(row, index)}` : '';
			add(field.id, `${field.label}${group} (row ${index + 1})`, row[target], row.address, row.phone, index);
		});
	}
	return people;
}

/** Copy only supported contact fields; retain the row's role and unrelated answers. */
export function copyAppointmentPerson(row: AppointmentRow, person: SavedAppointmentPerson, field: PersonField): AppointmentRow {
	const target = reusablePersonTarget(field);
	if (!target) return { ...row };
	const next = { ...row, [target]: person.full_name };
	for (const key of ['address', 'phone'] as const) {
		if (field.children?.some((child) => child.id === key)) next[key] = structuredClone(person[key]);
	}
	return next;
}
